import { useCallback, useEffect, useRef, useState } from "react";
import { HiMagnifyingGlassPlus } from "react-icons/hi2";
import FramedImage from "../FramedImage/FramedImage";
import { normalizeFraming } from "../../utilities/framing";

const MIN_SCALE = 1;

// A FramedImage the user can zoom right inside its window. Pinch is the
// only way in: two fingers on touch, or ctrl/cmd + scroll on desktop
// (which is what a trackpad pinch sends); pinching back out past fit is
// the way back. While zoomed, plain scroll also zooms, dragging pans
// (clipped by the window's overflow-hidden), and touch gestures stop
// propagating so the modal's swipe navigation doesn't fire mid-pan.
// Fills the nearest positioned ancestor, like FramedImage. `onTap`
// (with `tapLabel`) keeps the carousel's tap-anywhere-to-advance
// behaviour while unzoomed. `disabled` turns off zooming entirely (no
// gestures, no hint badge) but keeps the tap-to-advance overlay — only
// covers reward close inspection; stamps and articles stay fixed.
// Zoom stops at the scan's "original size": one image pixel per physical
// device pixel, past which there is no more real detail to reveal. A scan
// already displayed at or beyond that size can't be zoomed at all.
export default function ZoomableImage({
  src,
  alt,
  framing,
  imgClassName = "",
  onTap,
  tapLabel,
  disabled = false,
}) {
  const containerRef = useRef(null);
  // Active pointers, for pinch (two) vs pan (one)
  const pointersRef = useRef(new Map());
  const pinchDistRef = useRef(0);
  const [{ scale, x, y }, setTransform] = useState({ scale: 1, x: 0, y: 0 });
  const zoomed = scale > 1;
  // Mirror for the native wheel listener, which must decide synchronously
  // whether to hijack the scroll
  const scaleRef = useRef(scale);
  scaleRef.current = scale;

  // Intrinsic pixel size of the current scan, set when it loads (state,
  // so the hint badge can react to how much headroom the scan has)
  const [natural, setNatural] = useState(null);
  const naturalRef = useRef(null);
  naturalRef.current = natural;

  // Fresh image (e.g. carousel advance) starts back at fit.
  // Also pick up natural dimensions immediately if the image was already
  // in the browser cache — cached images fire the load event synchronously
  // during DOM insertion, before React's synthetic onLoad handler is
  // attached, so setNatural would otherwise never be called and zoom would
  // remain disabled until the page is refreshed or the modal is reopened.
  useEffect(() => {
    setNatural(null);
    setTransform({ scale: 1, x: 0, y: 0 });
    const img = containerRef.current?.querySelector("img");
    if (img?.complete && img.naturalWidth) {
      setNatural({ width: img.naturalWidth, height: img.naturalHeight });
    }
  }, [src]);

  // Re-render on container size changes (modal open animation, window
  // resize) so the badge's headroom check isn't stuck on a stale layout
  const [, setLayoutTick] = useState(0);
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => setLayoutTick((n) => n + 1));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const framingZoom = normalizeFraming(framing).zoom;

  // Scale at which one scan pixel spans one physical device pixel — the
  // "original size" cap. object-fit: cover already magnifies the scan by
  // max(container/natural) per axis, and the framing zoom stacks on that,
  // so the pinch gets whatever headroom remains. Read at gesture time:
  // container size and devicePixelRatio both change with the window.
  const getMaxScale = useCallback(() => {
    const el = containerRef.current;
    const nat = naturalRef.current;
    if (!el || !nat || !el.clientWidth || !el.clientHeight) return MIN_SCALE;
    const coverScale = Math.max(
      el.clientWidth / nat.width,
      el.clientHeight / nat.height,
    );
    return Math.max(
      MIN_SCALE,
      1 / (coverScale * framingZoom * (window.devicePixelRatio || 1)),
    );
  }, [framingZoom]);

  // Keep the image from being dragged fully out of the window
  const clampTransform = useCallback(
    (next) => {
      const el = containerRef.current;
      const clampedScale = Math.min(
        getMaxScale(),
        Math.max(MIN_SCALE, next.scale),
      );
      const maxX = el ? (el.clientWidth * (clampedScale - 1)) / 2 : 0;
      const maxY = el ? (el.clientHeight * (clampedScale - 1)) / 2 : 0;
      return {
        scale: clampedScale,
        x: Math.min(maxX, Math.max(-maxX, next.x)),
        y: Math.min(maxY, Math.max(-maxY, next.y)),
      };
    },
    [getMaxScale],
  );

  // Scale by `factor` keeping the screen point (clientX, clientY) fixed
  const zoomBy = useCallback(
    (clientX, clientY, factor) => {
      setTransform((t) => {
        const el = containerRef.current;
        if (!el) return t;
        const rect = el.getBoundingClientRect();
        const cx = clientX - rect.left - rect.width / 2;
        const cy = clientY - rect.top - rect.height / 2;
        const nextScale = Math.min(
          getMaxScale(),
          Math.max(MIN_SCALE, t.scale * factor),
        );
        const ratio = nextScale / t.scale;
        return clampTransform({
          scale: nextScale,
          x: cx - (cx - t.x) * ratio,
          y: cy - (cy - t.y) * ratio,
        });
      });
    },
    [clampTransform, getMaxScale],
  );

  // Wheel zoom needs preventDefault, so attach natively (React's root
  // wheel listeners are passive). Plain scrolling over an unzoomed image
  // keeps scrolling the modal; ctrl/cmd + wheel (also what a trackpad
  // pinch sends) zooms in, and once zoomed the wheel is all ours.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || disabled) return;
    function handleWheel(evt) {
      if (scaleRef.current <= 1 && !evt.ctrlKey && !evt.metaKey) return;
      // No headroom (scan already at original size): leave the browser's
      // own pinch/zoom behaviour alone instead of swallowing it
      if (scaleRef.current <= 1 && getMaxScale() <= MIN_SCALE) return;
      evt.preventDefault();
      zoomBy(evt.clientX, evt.clientY, Math.exp(-evt.deltaY * 0.002));
    }
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [zoomBy, getMaxScale, disabled]);

  // Belt and braces for the capture handoff in handlePointerDown: if a
  // pointerup or
  // pointercancel still escapes the container (e.g. a third finger, or a
  // browser that drops events for a removed capture target), sweep it up
  // at the window so a stale entry can't turn later one-finger drags
  // into phantom pinches.
  useEffect(() => {
    function sweep(evt) {
      if (pointersRef.current.delete(evt.pointerId)) {
        pinchDistRef.current = 0;
      }
    }
    window.addEventListener("pointerup", sweep);
    window.addEventListener("pointercancel", sweep);
    return () => {
      window.removeEventListener("pointerup", sweep);
      window.removeEventListener("pointercancel", sweep);
    };
  }, []);

  function handlePointerDown(evt) {
    if (disabled) return;
    pointersRef.current.set(evt.pointerId, {
      x: evt.clientX,
      y: evt.clientY,
    });
    const isPinch = pointersRef.current.size === 2;
    // Capture while zoomed (so a pan can keep going after the pointer
    // leaves the window) or the moment a second finger lands. The pinch
    // case matters on touch: a pinch that starts at fit begins on the
    // tap-to-advance button, whose implicit touch capture dies when the
    // first zoom render unmounts it — without re-capturing both pointers
    // here, a finger lifted outside the window never delivers its
    // pointerup. At fit with a single pointer, capturing would retarget
    // the click at the container, stealing it from the tap button.
    if (zoomed || isPinch) {
      const ids = isPinch ? [...pointersRef.current.keys()] : [evt.pointerId];
      for (const id of ids) {
        try {
          containerRef.current?.setPointerCapture?.(id);
        } catch {
          // Pointer may already be inactive (e.g. a cancelled touch)
        }
      }
    }
    if (isPinch) {
      const [a, b] = [...pointersRef.current.values()];
      pinchDistRef.current = Math.hypot(a.x - b.x, a.y - b.y);
    }
  }

  function handlePointerMove(evt) {
    const pointer = pointersRef.current.get(evt.pointerId);
    if (!pointer) return;
    const prevX = pointer.x;
    const prevY = pointer.y;
    pointer.x = evt.clientX;
    pointer.y = evt.clientY;
    if (pointersRef.current.size === 2) {
      const [a, b] = [...pointersRef.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinchDistRef.current > 0) {
        zoomBy((a.x + b.x) / 2, (a.y + b.y) / 2, dist / pinchDistRef.current);
      }
      pinchDistRef.current = dist;
    } else if (pointersRef.current.size === 1) {
      setTransform((t) =>
        t.scale > 1
          ? clampTransform({
              ...t,
              x: t.x + evt.clientX - prevX,
              y: t.y + evt.clientY - prevY,
            })
          : t,
      );
    }
  }

  function handlePointerUp(evt) {
    pointersRef.current.delete(evt.pointerId);
    pinchDistRef.current = 0;
  }

  function handlePointerCancel(evt) {
    pointersRef.current.delete(evt.pointerId);
    pinchDistRef.current = 0;
  }

  // While zoomed, touches are pans/pinches — keep them from reaching the
  // modal panel's swipe-to-navigate handlers
  const stopTouch = zoomed ? (evt) => evt.stopPropagation() : undefined;

  // Whether pinching in would do anything at all — a scan already shown
  // at original size (or one that hasn't loaded yet) has no headroom, so
  // the hint badge would be a lie
  const canZoom = !disabled && getMaxScale() > MIN_SCALE;

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 select-none"
      style={
        disabled
          ? undefined
          : {
              touchAction: zoomed ? "none" : "pan-y",
              cursor: zoomed ? "grab" : undefined,
            }
      }
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onTouchStart={stopTouch}
      onTouchMove={stopTouch}
      onTouchEnd={stopTouch}
    >
      <div
        className="absolute inset-0"
        style={{ transform: `translate(${x}px, ${y}px) scale(${scale})` }}
      >
        <FramedImage
          src={src}
          alt={alt}
          framing={framing}
          className={imgClassName}
          onLoad={(evt) =>
            setNatural({
              width: evt.target.naturalWidth,
              height: evt.target.naturalHeight,
            })
          }
        />
      </div>
      {onTap && !zoomed && (
        <button
          onClick={onTap}
          aria-label={tapLabel}
          className="absolute inset-0 z-10 cursor-pointer"
        />
      )}
      {/* Not a control — just a hint that the scan is zoomable */}
      {canZoom && !zoomed && (
        <div className="group absolute bottom-2 right-2 z-20 w-7 h-7 rounded-full bg-paper/70 border border-ink/10 text-ink/40 flex items-center justify-center">
          <HiMagnifyingGlassPlus className="w-3.5 h-3.5" />
          {/* Solid soft grey: what the metadata labels' ink-at-45% renders
              as over the paper background */}
          <span
            role="tooltip"
            className="pointer-events-none absolute bottom-full right-0 mb-2 whitespace-nowrap rounded-md bg-[#989490] text-paper text-xs px-2.5 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
          >
            Pinch image to zoom
          </span>
        </div>
      )}
    </div>
  );
}
